import { ComponentProps } from 'react';
import LoadingSpinner from '../LoadingSpinner';
import { cva, type VariantProps } from 'class-variance-authority';
import styles from './quizButton.module.scss';

export const sizeVariants = {
  responsive: styles.responsive
};

export const colorSchemeVariants = {
  primary: styles.primary,
  secondary: styles.secondary,
  destructive: styles.destructive,
  white: styles.white
} as const;

export const quizButtonClass = cva(styles.button, {
  variants: {
    size: sizeVariants,
    colorScheme: colorSchemeVariants,
    loading: {
      true: styles.loading
    }
  },
  defaultVariants: {
    colorScheme: 'primary',
    size: 'responsive'
  }
});

type QuizButtonProps = ComponentProps<'button'> &
  VariantProps<typeof quizButtonClass> & {
    loading?: boolean;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
  };

export default function QuizButton({
  className,
  loading = false,
  colorScheme = 'primary',
  size = 'responsive',
  startIcon = null,
  endIcon = null,
  children,
  ...props
}: QuizButtonProps) {
  const renderChildren = () => {
    if (startIcon || endIcon) {
      return (
        <div className={styles.withIcon}>
          {startIcon && <span className={styles.startIcon}>{startIcon}</span>}
          <span className={styles.textContainer}>{children}</span>
          {endIcon && <span className={styles.endIcon}>{endIcon}</span>}
        </div>
      );
    }
    return children;
  };

  if (loading) {
    return (
      <button
        className={quizButtonClass({
          className,
          colorScheme,
          size,
          loading
        })}
        {...props}
      >
        <div className={styles.spinner}>
          <LoadingSpinner size="small" />
        </div>
        <div className={styles.loadingText}>{renderChildren()}</div>
      </button>
    );
  }
  return (
    <button
      className={quizButtonClass({ className, colorScheme, size })}
      {...props}
    >
      {renderChildren()}
    </button>
  );
}
